// **The instruct chapter's prompt bar** (`demo-presentation-shell` tasks 10.1-10.6, design **DD6**
// / **DD7**). Chapter 1b's "Bees" scope: submits to `POST /demo/instruct` (contract C-2),
// dispatches every returned command into the local wasm world through the same override surface
// the right-click chapter uses, and holds exactly one call in flight.
//
// **The idempotency-key retry policy (D-262, closed) — read before touching this file.**
//
// This file used to carry a flagged, unresolved contradiction: the change's own `design.md`
// (**DD7**), `specs/client-demo/spec.md` and task 10.3's covers marker all said "a retry after a
// **timeout** reuses the same key", while the programme's C-2 contract
// (`docs/dev/plans/demo/presentation-mode.md` §C-2, **RF1**) said the opposite for a *server-side*
// timeout — and so did the already-committed group-1 double (`test/fake-instruct-transport.ts`'s
// `InstructTimeoutBody`, which carries a `retryKey`).
//
// **RF1 is the authority, and the server already implements it**, so the shell's artifacts were the
// divergence and have been corrected (DD7 carries the correction note; the one spec scenario became
// two). A server-side timeout *settles* and is cached under the original key, and its response
// instructs a fresh one — so replaying the original could only replay the timeout, and the failure
// state's "Try again" would have been an affordance that could not succeed.
//
// The rule below is therefore split by **which failure is being retried**, because they are not the
// same failure:
//
//   * a **transport** failure (`unavailable`: the request never completed, so whether the server saw
//     it is unknown) retries with the **same** key — this is the case the field exists for, and it
//     is what stops a request that in fact landed from being charged twice; and
//   * an answered **timeout** retries with the **server-issued `retry_key`**.
//
// Everything else that fails (a refusal, budget exhaustion, a malformed body) is a settled answer
// with no retry key of its own, so it keeps the key it was asked under.

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { DemoStageDriver } from './stage-driver';
import { PromptBar } from './PromptBar';
import type { PromptBarStatus } from './PromptBar';
import {
  buildInstructRequestBody,
  postInstruct,
  toBeeCommands,
  type InstructOutcome,
  type InstructTranscriptEntry,
} from './instruct-client';

export interface InstructPromptBarProps {
  driver: DemoStageDriver;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The idempotency-key seam — defaults to `crypto.randomUUID()`, overridden in tests for a
   * deterministic assertion. */
  idGen?: () => string;
  /**
   * **Clears a stale failure/reply when the show moves to a different beat** (E2E round-1 finding,
   * 2026-08-09). This bar is keyed by chapter (`DemoApp.tsx`), which resets it across a chapter
   * boundary — but *within* chapter 1, nothing did: chapter 1b's failed instruct ("The bees didn't
   * hear that one.") rode under the "back to work" beat and the enemy raid that follows it, all
   * still inside chapter 1. The sequencer's own cursor — a plain, monotonically-moving integer
   * (`DemoApp.tsx`'s own note on it) — is the finer-grained signal: any change means the show has
   * moved to a different beat, so the failure/reply DISPLAY clears. The presenter's own in-progress
   * draft is untouched — only what a *previous* submission left behind. Omitted, nothing clears
   * (the bar's original behaviour), which is what every existing render site that has no cursor to
   * offer keeps getting.
   */
  cursor?: number;
}

function defaultIdGen(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `instruct-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function failureMessageFor(outcome: Extract<InstructOutcome, { kind: 'refused' | 'timeout' | 'budgetExhausted' | 'malformed' | 'unavailable' }>): string {
  switch (outcome.kind) {
    case 'refused':
      return "The bees didn't hear that one.";
    case 'timeout':
      return 'That took too long — try again.';
    case 'budgetExhausted':
      return "The hive's out of prompts for now.";
    case 'malformed':
      return 'Something went wrong relaying that.';
    case 'unavailable':
      return "Couldn't reach the hive.";
  }
}

export function InstructPromptBar({ driver, baseUrl, fetchImpl, idGen, cursor }: InstructPromptBarProps): ReactElement {
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<PromptBarStatus>('idle');
  const [failureMessage, setFailureMessage] = useState<string | undefined>(undefined);
  const [replyText, setReplyText] = useState<string | undefined>(undefined);
  const transcriptRef = useRef<InstructTranscriptEntry[]>([]);
  const pendingRef = useRef<{ key: string; text: string } | null>(null);
  const busyRef = useRef(false);

  // See `InstructPromptBarProps.cursor`'s own note. Guarded on an actual change (not merely a
  // mount with `cursor` already set) so a bar that never moves clears nothing it did not already
  // hold — `useRef(cursor)`'s initial value IS this render's value, so the very first effect run
  // never sees a change.
  const lastCursorRef = useRef(cursor);
  useEffect(() => {
    if (cursor === lastCursorRef.current) {
      return;
    }
    lastCursorRef.current = cursor;
    setStatus((current) => (current === 'failed' ? 'idle' : current));
    setFailureMessage(undefined);
    setReplyText(undefined);
    // The draft is deliberately left alone — the presenter may be mid-sentence.
  }, [cursor]);

  async function run(key: string, text: string): Promise<void> {
    busyRef.current = true;
    setStatus('busy');
    setFailureMessage(undefined);
    let outcome: InstructOutcome;
    try {
      const world = await driver.worldForInstruct?.();
      const body = buildInstructRequestBody({
        idempotencyKey: key,
        text,
        world,
        transcript: transcriptRef.current,
      });
      outcome = await postInstruct({
        // **`globalThis.fetch.bind(globalThis)`, never a bare `fetch`** (E2E finding N1). A
        // detached `fetch` reference throws `Illegal invocation` in a real browser — the whole
        // chapter-1b instruct died client-side, before a single request left the page, and every
        // test passed because jsdom's `fetch` (and every stubbed one) does not care about `this`.
        // The same bind every other client in `net/` already writes.
        fetchImpl: fetchImpl ?? globalThis.fetch.bind(globalThis),
        baseUrl,
        body,
      });
    } catch (error) {
      // A rejected **world read** is the other way this can fail before the wire, and it used to
      // leave `busyRef` latched true and the bar stuck on "Working…" for the rest of the show.
      // Same class as a transport that will not answer: reported, and retryable.
      outcome = {
        kind: 'unavailable',
        message: error instanceof Error ? error.message : 'the stage did not answer',
      };
    } finally {
      busyRef.current = false;
    }

    if (outcome.kind === 'ok') {
      // One entry per completed turn — the server's own `{text, reply}` shape.
      transcriptRef.current = [...transcriptRef.current, { text, reply: outcome.reply }];
      for (const { beeIds, command } of toBeeCommands(outcome.commands)) {
        void driver.submitCommand({ beeIds, command });
      }
      pendingRef.current = null;
      setStatus('idle');
      setReplyText(outcome.reply);
      setDraft('');
      return;
    }

    // Every other outcome kind carries no `ok`-only fields, so the switch above already narrowed
    // `outcome` to the failure union `failureMessageFor` accepts.
    //
    // **What the retry will be keyed with** (RF1 / D-262, see the module header): an *answered*
    // timeout is settled and cached under `key`, and names the key a retry must use instead — so
    // the pending attempt records that one. Every other failure, transport included, keeps the key
    // it was asked under.
    // The presenter gets one short line by design (`demo-presenter-guide.md` §5), which collapses
    // ten distinct server reasons into "The bees didn't hear that one." — so the machine reason,
    // which the response does carry, goes to the console instead of being thrown away. Not on
    // screen: a raw reason in front of an audience is exactly what that surface refuses to be.
    console.warn('demo instruct refused', outcome);
    pendingRef.current = {
      key: outcome.kind === 'timeout' && outcome.retryKey.length > 0 ? outcome.retryKey : key,
      text,
    };
    setStatus('failed');
    setFailureMessage(failureMessageFor(outcome));
  }

  return (
    <PromptBar
      scopeLabel="Bees"
      draft={draft}
      status={status}
      failureMessage={failureMessage}
      replyText={replyText}
      // The reply-bubble queue lives inside `PromptBar` itself (see its own module header for the
      // draft-edit-vs-beat-change decision); this bar's own `cursor` is the beat-change signal it
      // clears the queue on.
      resetKey={cursor}
      onChangeDraft={(text) => {
        setDraft(text);
        setReplyText(undefined);
      }}
      onSubmit={() => {
        // Single-in-flight, locally: a submit while `busyRef` is still true from a not-yet-settled
        // call is refused with no second request — `PromptBar` already hides Send/disables the
        // input while `status === 'busy'`, this is the defensive second guard against a race.
        if (busyRef.current) {
          return;
        }
        void run(idGen?.() ?? defaultIdGen(), draft);
      }}
      onRetry={() => {
        const pending = pendingRef.current;
        if (pending === null || busyRef.current) {
          return;
        }
        // The key the *failure* named (see module header): the original for a transport failure,
        // the server's `retry_key` for an answered timeout. Never a freshly generated one.
        void run(pending.key, pending.text);
      }}
    />
  );
}
